import { getCountries, getCountryCallingCode } from "libphonenumber-js";

// Regional-indicator flag emoji built from the ISO 3166-1 alpha-2 code —
// no flag image assets/CDN needed. Some libphonenumber-js codes (e.g. "AC",
// "TA", "XK") aren't officially assigned an emoji flag by Unicode and will
// render as plain letters on some platforms; the dial code text next to it
// is always shown too, so the country is never identified by the flag alone.
function isoToFlagEmoji(iso2) {
  return String.fromCodePoint(
    ...[...iso2.toUpperCase()].map((char) => 127397 + char.charCodeAt(0)),
  );
}

function countryDisplayNames() {
  try {
    return new Intl.DisplayNames(["uk"], { type: "region" });
  } catch {
    return null;
  }
}

function buildCountries() {
  const displayNames = countryDisplayNames();

  return getCountries()
    .map((iso2) => ({
      iso2,
      name: displayNames?.of(iso2) || iso2,
      dialCode: getCountryCallingCode(iso2),
      flag: isoToFlagEmoji(iso2),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "uk"));
}

// Built once per bundle load — libphonenumber-js metadata and
// Intl.DisplayNames are both static for the session.
export const COUNTRIES = buildCountries();

const BY_ISO2 = new Map(COUNTRIES.map((country) => [country.iso2, country]));

export function findCountry(iso2) {
  if (!iso2) return undefined;
  return BY_ISO2.get(iso2.toUpperCase());
}

// ISO 3166-1 codes for the rest of Europe (UN geoscheme, plus the small
// territories libphonenumber-js tracks separately — Åland, Faroe, Svalbard,
// the Channel Islands). Deliberately excludes Russia and Belarus: pinning
// either at the top of a priority list on a Ukrainian product during the
// war would be a tone-deaf default regardless of raw geography. Both stay
// fully reachable through search, just not specially promoted.
const REST_OF_EUROPE = new Set([
  "AD", "AL", "AT", "AX", "BA", "BE", "BG", "CH", "CY", "CZ", "DE", "DK",
  "EE", "ES", "FI", "FO", "FR", "GB", "GG", "GI", "GR", "HR", "HU", "IE",
  "IM", "IS", "IT", "JE", "LI", "LT", "LU", "LV", "MC", "MD", "ME", "MK",
  "MT", "NL", "NO", "PL", "PT", "RO", "RS", "SE", "SI", "SJ", "SK", "SM",
  "VA", "XK",
]);

// Countries pinned above the alphabetical list when the picker first opens
// (CountrySelect's `preferredCountries` prop defaults to this): Ukraine,
// since that's this product's audience, then the rest of Europe, in the
// same Ukrainian-alphabetical order as the full list — most of the
// remaining users are typing a number for someone calling from abroad.
export const DEFAULT_PREFERRED_COUNTRIES = [
  "UA",
  ...COUNTRIES.filter((c) => REST_OF_EUROPE.has(c.iso2)).map((c) => c.iso2),
];

export function searchCountries(query) {
  const normalized = query.trim().toLowerCase().replace(/^\+/, "");
  if (!normalized) return COUNTRIES;

  return COUNTRIES.filter(
    (country) =>
      country.name.toLowerCase().includes(normalized) ||
      country.dialCode.includes(normalized) ||
      country.iso2.toLowerCase() === normalized,
  );
}
